import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactLoading from "react-loading";
import * as XLSX from "xlsx";
import Header from "./Header";
import upload_icon from "../assets/upload.svg";

import {
  annotateText,
  annotateTexts,
  COLORS,
  CONTEXT_CATEGORIES,
  splitParagraphIntoSentences,
} from "./CelestaUtility";

// BioRECIPE interaction list columns
// Mapping: CELESTA context → BioRECIPE column
//   location  → Regulator/Regulated Compartment
//   organ     → Tissue Type (mapped to Organism in BioRECIPE is species)
//   species   → Organism
//   relation  → Mechanism
//   Sign: Inhibition → negative, else → positive
//   Connection Type: default "indirect"
// hidden: true = hidden by default, user can toggle to show
const INTERACTION_FIELDS = [
  { key: "statement", label: "Statements", required: true },
  { key: "regulatorName", label: "Regulator Name", required: false },
  {
    key: "regulatorType",
    label: "Regulator Type",
    required: false,
    hidden: true,
  },
  {
    key: "regulatorSubtype",
    label: "Regulator Subtype",
    required: false,
    hidden: true,
  },
  {
    key: "regulatorHGNC",
    label: "Regulator HGNC Symbol",
    required: false,
    hidden: true,
  },
  {
    key: "regulatorDatabase",
    label: "Regulator Database",
    required: false,
    hidden: true,
  },
  { key: "regulatorID", label: "Regulator ID", required: false, hidden: true },
  {
    key: "regulatorCompartment",
    label: "Regulator Compartment",
    required: false,
  },
  {
    key: "regulatorCompartmentID",
    label: "Regulator Compartment ID",
    required: false,
    hidden: true,
  },
  { key: "regulatedName", label: "Regulated Name", required: false },
  {
    key: "regulatedType",
    label: "Regulated Type",
    required: false,
    hidden: true,
  },
  {
    key: "regulatedSubtype",
    label: "Regulated Subtype",
    required: false,
    hidden: true,
  },
  {
    key: "regulatedHGNC",
    label: "Regulated HGNC Symbol",
    required: false,
    hidden: true,
  },
  {
    key: "regulatedDatabase",
    label: "Regulated Database",
    required: false,
    hidden: true,
  },
  { key: "regulatedID", label: "Regulated ID", required: false, hidden: true },
  {
    key: "regulatedCompartment",
    label: "Regulated Compartment",
    required: false,
  },
  {
    key: "regulatedCompartmentID",
    label: "Regulated Compartment ID",
    required: false,
    hidden: true,
  },
  { key: "sign", label: "Sign", required: false },
  {
    key: "connectionType",
    label: "Connection Type",
    required: false,
    hidden: true,
  },
  { key: "mechanism", label: "Mechanism", required: false },
  { key: "site", label: "Site", required: false, hidden: true },
  { key: "cellLine", label: "Cell Line", required: false },
  { key: "cellType", label: "Cell Type", required: false },
  { key: "tissueType", label: "Tissue Type", required: false, hidden: true },
  { key: "organism", label: "Organism", required: false },
  { key: "disease", label: "Disease", required: false },
  { key: "score", label: "Score", required: false, hidden: true },
  { key: "source", label: "Source", required: false, hidden: true },
  { key: "paperIDs", label: "Paper IDs", required: false, hidden: true },
];

const DEFAULT_HIDDEN = new Set(
  INTERACTION_FIELDS.filter((f) => f.hidden).map((f) => f.key),
);

// Map uploaded xlsx column headers (case-insensitive) → internal keys
const COLUMN_MAP = {};
INTERACTION_FIELDS.forEach((f) => {
  COLUMN_MAP[f.label.toLowerCase()] = f.key;
});
// Extra aliases (don't conflict with real column names)
Object.assign(COLUMN_MAP, {
  statement: "statement",
  evidence: "statement",
  text: "statement",
  "element name": "regulatorName",
  organ: "tissueType",
  species: "organism",
});

function mapUploadedRow(rawRow) {
  const row = {};
  INTERACTION_FIELDS.forEach((f) => {
    row[f.key] = "";
  });
  Object.entries(rawRow).forEach(([col, val]) => {
    const mapped = COLUMN_MAP[col.toLowerCase().trim()];
    if (mapped && val != null) row[mapped] = String(val);
  });
  return row;
}

function createEmptyRow() {
  const row = {};
  INTERACTION_FIELDS.forEach((f) => {
    row[f.key] = "";
  });
  // Defaults
  row.connectionType = "indirect";
  return row;
}

const VALID_MECHANISMS = ["Activation", "Inhibition", "Phosphorylation"];

// After annotation, map CELESTA results back to BioRECIPE fields
function applyAnnotationToRow(row, annotation) {
  if (!annotation) return { row, changed: [] };
  const updated = { ...row };

  // Helper: treat empty, whitespace-only, "NONE" as empty
  const isEmpty = (v) => !v || !v.trim() || v.trim().toUpperCase() === "NONE";

  // Entities
  if (annotation.entities) {
    const entA = annotation.entities.find((e) => e.role === "A");
    const entB = annotation.entities.find((e) => e.role === "B");
    if (entA && isEmpty(updated.regulatorName))
      updated.regulatorName = entA.name;
    if (entA && isEmpty(updated.regulatorType))
      updated.regulatorType = entA.entityType || "";
    if (entB && isEmpty(updated.regulatedName))
      updated.regulatedName = entB.name;
    if (entB && isEmpty(updated.regulatedType))
      updated.regulatedType = entB.entityType || "";
  }

  // Mechanism / Sign / Relation logic:
  // 1. If row has valid mechanism (Activation/Inhibition/Phosphorylation) → keep it
  // 2. If mechanism is invalid (NONE, empty, etc.) → derive from annotation.relation or sign
  const existingMech = updated.mechanism?.trim();
  const mechIsNone = !existingMech || existingMech.toUpperCase() === "NONE";
  const isValidMech = !mechIsNone && VALID_MECHANISMS.includes(existingMech);

  if (!isValidMech) {
    if (annotation.relation && VALID_MECHANISMS.includes(annotation.relation)) {
      // Use annotation relation as mechanism
      updated.mechanism = annotation.relation;
    } else if (updated.sign) {
      // Derive from sign
      updated.mechanism =
        updated.sign.toLowerCase() === "negative" ? "Inhibition" : "Activation";
    }
  }

  // Fill sign from mechanism if missing
  if (!updated.sign && updated.mechanism) {
    updated.sign = updated.mechanism === "Inhibition" ? "negative" : "positive";
  }

  if (!updated.connectionType) updated.connectionType = "indirect";

  // Context → BioRECIPE columns
  // Multiple labels for same category → comma-separated
  const appendVal = (current, label) => {
    if (isEmpty(current)) return label;
    // Don't add duplicate
    const existing = current.split(", ").map((s) => s.trim());
    if (existing.includes(label)) return current;
    return current + ", " + label;
  };

  if (annotation.contexts) {
    annotation.contexts.forEach((ctx) => {
      switch (ctx.category) {
        case "location":
          updated.regulatorCompartment = appendVal(
            updated.regulatorCompartment,
            ctx.label,
          );
          updated.regulatedCompartment = appendVal(
            updated.regulatedCompartment,
            ctx.label,
          );
          break;
        case "cell_line":
          updated.cellLine = appendVal(updated.cellLine, ctx.label);
          break;
        case "cell_type":
          updated.cellType = appendVal(updated.cellType, ctx.label);
          break;
        case "organ":
          updated.tissueType = appendVal(updated.tissueType, ctx.label);
          break;
        case "disease":
          updated.disease = appendVal(updated.disease, ctx.label);
          break;
        case "species":
          updated.organism = appendVal(updated.organism, ctx.label);
          break;
        default:
          break;
      }
    });
  }

  // Collect which fields were actually changed
  const changed = [];
  Object.keys(updated).forEach((k) => {
    if (updated[k] !== row[k]) changed.push(k);
  });

  // Debug: log what changed
  if (changed.length)
    console.log(
      "[CELESTA] applyAnnotation changed:",
      changed,
      "contexts:",
      annotation.contexts?.map((c) => `${c.category}=${c.label}`),
    );

  return { row: updated, changed };
}

// Build entity-only segments (contexts shown as citations now)
function buildEntitySegments(row, annotation) {
  const text = row.statement;
  if (!text) return [];
  const markers = [];
  if (row.regulatorName) {
    const idx = text.indexOf(row.regulatorName);
    if (idx !== -1)
      markers.push({
        start: idx,
        end: idx + row.regulatorName.length,
        type: "entity",
        role: "A",
        name: row.regulatorName,
      });
  }
  if (row.regulatedName) {
    const idx = text.indexOf(row.regulatedName);
    if (idx !== -1)
      markers.push({
        start: idx,
        end: idx + row.regulatedName.length,
        type: "entity",
        role: "B",
        name: row.regulatedName,
      });
  }
  if (annotation?.entities) {
    annotation.entities.forEach((ent) => {
      // Skip if overlaps with any existing marker (row entities take priority)
      const overlaps = markers.some(
        (m) => ent.start < m.end && ent.end > m.start,
      );
      if (!overlaps)
        markers.push({
          start: ent.start,
          end: ent.end,
          type: "entity",
          role: ent.role,
          name: ent.name,
          entityType: ent.entityType || "",
        });
    });
  }
  if (markers.length === 0) return [{ text, type: "plain" }];
  markers.sort((a, b) => a.start - b.start);
  // Remove overlaps
  const final = [markers[0]];
  for (let i = 1; i < markers.length; i++) {
    if (markers[i].start >= final[final.length - 1].end) final.push(markers[i]);
  }
  const segs = [];
  let cursor = 0;
  final.forEach((m) => {
    if (m.start > cursor)
      segs.push({ text: text.slice(cursor, m.start), type: "plain" });
    segs.push({
      text: text.slice(m.start, m.end),
      type: "entity",
      role: m.role,
      name: m.name,
      entityType: m.entityType || "",
    });
    cursor = m.end;
  });
  if (cursor < text.length)
    segs.push({ text: text.slice(cursor), type: "plain" });
  return segs;
}

// ============================================================
// Styles
// ============================================================
const styles = `
.cel { display:flex; flex-direction:column; font-family:Arial,sans-serif;   min-height: calc(100vh - 64px);
; overflow:hidden; }

/* ---- Quick mode: input top-center, cards below ---- */
.cel-quick { flex:1; display:flex; flex-direction:column; overflow:hidden; }

.cel-input-bar { max-width:760px; margin:0 auto; padding:24px 24px 12px; width:100%; box-sizing:border-box; flex:0 0 auto; }

.cel-input-description { font-size:14px; color:#888; text-align:center; margin:0 0 10px; }

.cel-cards-scroll { flex:1; overflow-y:auto; padding:0 0 24px; }

.cel-cards-inner { max-width:760px; margin:0 auto; display:flex; flex-direction:column; gap:16px; padding:0 24px; }

.cel-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; color:#bbb; font-size:15px; text-align:center; }
.cel-empty-icon { font-size:40px; margin-bottom:12px; }

.cel-input-row { display:flex; align-items:flex-end; gap:10px; background:#f5f5f5; border:1px solid #ccc; border-radius:12px; padding:10px 14px; transition:border-color 0.2s; }
.cel-input-row:focus-within { border-color:#003594; }

.cel-input-text { flex:1; border:none; background:transparent; font-size:15px; font-family:inherit; line-height:1.5; resize:none; outline:none; max-height:120px; min-height:24px; }
.cel-input-text::placeholder { color:#999; }

.cel-input-upload { background:none; border:none; cursor:pointer; padding:4px; opacity:0.5; transition:opacity 0.2s; display:flex; align-items:center; }
.cel-input-upload:hover { opacity:1; }
.cel-input-upload img { width:20px; height:20px; }

.cel-input-send { background:#003594; border:none; color:#fff; width:36px; height:36px; border-radius:8px; cursor:pointer; font-size:18px; display:flex; align-items:center; justify-content:center; transition:background 0.2s; flex-shrink:0; }
.cel-input-send:hover { background:#00205B; }
.cel-input-send:disabled { background:#ccc; cursor:not-allowed; }

/* ---- Annotation Card: fixed small margin between border and canvas ---- */
.cel-card { padding:8px 12px; border:1px solid #e8e8e8; border-radius:10px; background:#fff; transition:border-color 0.2s, box-shadow 0.2s; }
.cel-card:hover { border-color:#ccc; }
.cel-card.active { border-color:#003594; box-shadow:0 0 0 1px #003594; }
.cel-card-index { display:flex; align-items:center; justify-content:space-between; gap:8px; font-size:10px; color:#aaa; margin-bottom:4px; }
.cel-card-pending { color:#003594; font-weight:600; }
.cel-card-empty { color:#bbb; font-style:italic; font-size:14px; padding:8px 0; }

/* Brat-style highlight: light colored bg + dark tag above */
.cel-hl { position:relative; display:inline; padding:1px 2px; border-bottom:2px solid; border-radius:2px; }
.cel-hl-tag { position:absolute; top:-16px; left:50%; transform:translateX(-50%); font-size:11px; font-weight:600; padding:0px 4px; border-radius:2px; border:1px solid; white-space:nowrap; line-height:1.4; }

/* Annotation canvas: dynamic — grows with tags above, brace below */
.cel-text-block { position:relative; padding-top:18px; padding-bottom:2px; }
.cel-text-content { font-size:14px; line-height:2.8; color:#333; }

/* Relation arc SVG overlay */
.cel-arc-svg { position:absolute; top:0; left:0; width:100%; pointer-events:none; overflow:visible; }

/* Context trigger highlight (when citation clicked) — same brat style */
.cel-trigger-hl { position:relative; display:inline; padding:1px 2px; border-bottom:2px solid; border-radius:2px; }
.cel-trigger-tag { position:absolute; top:-16px; left:50%; transform:translateX(-50%); font-size:11px; font-weight:600; padding:0px 4px; border-radius:2px; border:1px solid; white-space:nowrap; line-height:1.4; }

/* Inline citations after text */
.cel-citations { display:inline; margin-left:4px; }
.cel-cite { display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; border-radius:50%; font-size:9px; font-weight:700; color:#fff; cursor:pointer; margin:0 1px; vertical-align:super; transition:transform 0.15s, box-shadow 0.15s; }
.cel-cite:hover { transform:scale(1.15); box-shadow:0 0 0 2px rgba(0,0,0,0.1); }

/* Implicit context: parentheses around text + labels below */
.cel-implicit-paren { font-weight:700; font-size:1.1em; }
.cel-implicit-label-row { display:flex; flex-wrap:wrap; gap:4px; justify-content:center; margin-top:0; padding:0; }
.cel-implicit-label { display:inline-block; font-size:10px; font-weight:600; padding:1px 6px; border-radius:3px; border:1px solid; }

/* Dimmed citation dot (toggled off) */
.cel-cite.off { opacity:0.3; }

/* ---- Legend ---- */
.cel-legend { display:flex; flex-wrap:wrap; gap:10px; padding:8px 0; font-size:11px; }
.cel-legend-item { display:flex; align-items:center; gap:4px; color:#666; }
.cel-legend-swatch { width:10px; height:10px; border-radius:50%; border:1px solid; }

/* ---- Bottom mode toggle ---- */
.cel-mode-bar { display:flex; align-items:center; justify-content:center; gap:12px; padding:8px 16px; border-top:1px solid #eee; background:#fafafa; }
.cel-mode-toggle { display:flex; border:1px solid #ccc; border-radius:6px; overflow:hidden; }
.cel-mode-toggle button { padding:5px 16px; border:none; background:#fff; color:#666; font-size:12px; cursor:pointer; transition:all 0.15s; }
.cel-mode-toggle button:not(:last-child) { border-right:1px solid #eee; }
.cel-mode-toggle button.active { background:#003594; color:#fff; }
.cel-mode-toggle button:hover:not(.active) { background:#f0f4ff; }
.cel-mode-hint { font-size:11px; color:#aaa; }

/* ---- Workspace mode ---- */
.cel-workspace { flex:1; display:flex; flex-direction:column; padding:12px 16px 0; gap:12px; overflow:hidden; }
.cel-ws-toolbar { display:flex; align-items:center; gap:10px; flex:0 0 auto; }
.cel-ws-body { flex:1; display:flex; gap:16px; min-height:0; overflow:hidden; }
.cel-btn { padding:8px 20px; border:1px solid #333; border-radius:6px; background:#fff; color:#333; font-size:14px; font-weight:500; cursor:pointer; transition:all 0.2s; }
.cel-btn:hover { background:#003594; color:#fff; border-color:#003594; }
.cel-btn:disabled { opacity:0.4; cursor:not-allowed; }
.cel-btn:disabled:hover { background:#fff; color:#333; border-color:#333; }
.cel-icon-btn { display:flex; align-items:center; gap:6px; padding:8px 14px; border:1px solid #ccc; border-radius:6px; background:#fff; color:#555; font-size:13px; cursor:pointer; transition:all 0.2s; }
.cel-icon-btn:hover { border-color:#003594; color:#003594; }
.cel-icon-btn:disabled { opacity:0.4; cursor:not-allowed; }
.cel-icon-btn img { width:15px; height:15px; }
.cel-spacer { flex:1; }

/* Left panel */
.cel-left { flex:1; display:flex; flex-direction:column; min-width:0; overflow:hidden; border:1px solid #ddd; border-radius:8px; background:#fff; }
.cel-left-header { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:#fafafa; border-bottom:1px solid #eee; font-size:13px; font-weight:600; color:#333; position:relative; }
.cel-col-toggle-btn { background:none; border:1px solid #ccc; border-radius:4px; padding:2px 8px; font-size:11px; color:#555; cursor:pointer; }
.cel-col-toggle-btn:hover { border-color:#003594; color:#003594; }
.cel-col-dropdown { position:absolute; top:100%; right:8px; background:#fff; border:1px solid #ddd; border-radius:6px; box-shadow:0 4px 12px rgba(0,0,0,0.1); z-index:20; padding:6px 0; max-height:300px; overflow-y:auto; min-width:200px; }
.cel-col-item { display:flex; align-items:center; gap:6px; padding:4px 12px; font-size:12px; color:#333; cursor:pointer; }
.cel-col-item:hover { background:#f5f8ff; }
.cel-col-item input { margin:0; }
.cel-left-scroll { flex:1; overflow:auto; }
.cel-edit-table { width:100%; border-collapse:collapse; font-size:12px; }
.cel-edit-table th { background:#003594; color:#fff; padding:4px 5px; text-align:left; font-weight:600; position:sticky; top:0; z-index:2; white-space:nowrap; font-size:10px; }
.cel-edit-table th.required::after { content:" *"; color:#FFB81C; }
.cel-edit-table td { padding:2px 3px; border-bottom:1px solid #f0f0f0; vertical-align:top; }
.cel-edit-table tr:hover td { background:#f5f8ff; }
.cel-edit-table tr.cel-row-selected td { background:#DBEEFF; }
.cel-edit-table tr.cel-row-empty td { background:#fff8f0; }
.cel-cell-input { width:100%; border:1px solid transparent; border-radius:3px; padding:2px 3px; font-size:11px; font-family:inherit; outline:none; background:transparent; box-sizing:border-box; transition:border-color 0.15s; }
.cel-cell-input:focus { border-color:#003594; background:#fff; }
.cel-cell-input.dirty { background:#FFF8E1; }
.cel-cell-input.statement-input { min-width:140px; }
.cel-row-num { width:32px; text-align:center; color:#999; font-size:11px; cursor:pointer; }
.cel-add-row-btn { width:100%; padding:8px; border:none; background:#fafafa; color:#003594; font-size:13px; font-weight:600; cursor:pointer; transition:background 0.2s; }
.cel-add-row-btn:hover { background:#e8eeff; }

/* Right panel */
.cel-right { flex:1; display:flex; flex-direction:column; min-width:0; overflow:hidden; border:1px solid #ddd; border-radius:8px; background:#fff; }
.cel-right-header { padding:8px 12px; background:#fafafa; border-bottom:1px solid #eee; font-size:13px; font-weight:600; color:#333; }
.cel-right-scroll { flex:1; overflow-y:auto; padding:12px; display:flex; flex-direction:column; gap:12px; }

/* Loading */
.cel-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px; color:#888; }

/* Warning modal */
.cel-modal-overlay { position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.4); z-index:100; display:flex; align-items:center; justify-content:center; }
.cel-modal { background:#fff; border-radius:12px; padding:28px 32px; max-width:420px; width:90%; box-shadow:0 8px 32px rgba(0,0,0,0.2); }
.cel-modal h3 { margin:0 0 12px; font-size:16px; color:#333; }
.cel-modal p { margin:0 0 20px; font-size:14px; color:#666; line-height:1.5; }
.cel-modal-actions { display:flex; gap:10px; justify-content:flex-end; }
.cel-modal-actions button { padding:8px 20px; border-radius:6px; font-size:14px; cursor:pointer; transition:all 0.2s; }
.cel-modal-cancel { border:1px solid #ccc; background:#fff; color:#333; }
.cel-modal-cancel:hover { background:#f5f5f5; }
.cel-modal-export { border:1px solid #036b45; background:#036b45; color:#fff; }
.cel-modal-export:hover { background:#024d32; }
.cel-modal-discard { border:1px solid #c62828; background:#fff; color:#c62828; }
.cel-modal-discard:hover { background:#ffebee; }
`;

// ============================================================
// Sub-components
// ============================================================

function Legend() {
  return (
    <div className="cel-legend">
      <div className="cel-legend-item">
        <div
          className="cel-legend-swatch"
          style={{
            background: COLORS.regulator.bg,
            borderColor: COLORS.regulator.border,
          }}
        />
        regulator
      </div>
      <div className="cel-legend-item">
        <div
          className="cel-legend-swatch"
          style={{
            background: COLORS.regulated.bg,
            borderColor: COLORS.regulated.border,
          }}
        />
        regulated
      </div>
      {CONTEXT_CATEGORIES.map((key) => {
        const c = COLORS[key];
        return (
          <div className="cel-legend-item" key={key}>
            <div
              className="cel-legend-swatch"
              style={{ background: c.text, borderColor: c.border }}
            />
            {key.replace(/_/g, " ")}
          </div>
        );
      })}
    </div>
  );
}

// ---- Reusable: compute arc between two tag elements ----
function computeArc(containerEl, aEl, bEl) {
  if (!containerEl || !aEl || !bEl) return null;
  const aTag = aEl.querySelector(".cel-hl-tag");
  const bTag = bEl.querySelector(".cel-hl-tag");
  const cRect = containerEl.getBoundingClientRect();
  const aBox = (aTag || aEl).getBoundingClientRect();
  const bBox = (bTag || bEl).getBoundingClientRect();
  const x1 = aBox.left + aBox.width / 2 - cRect.left;
  const y1 = aBox.top - cRect.top;
  const x2 = bBox.left + bBox.width / 2 - cRect.left;
  const y2 = bBox.top - cRect.top;
  const midX = (x1 + x2) / 2;
  const dist = Math.abs(x2 - x1);
  const arcH = Math.max(10, Math.min(dist * 0.15, 18));
  const midY = Math.min(y1, y2) - arcH;
  return { x1, y1, x2, y2, midX, midY };
}

function AnnotationCard({
  row,
  annotation,
  index,
  isActive,
  onClick,
  cardRef,
  pending = false,
}) {
  const text = row.statement?.trim() || "";
  const segments = buildEntitySegments(row, annotation);
  // Relation: prefer row.mechanism if valid, then annotation.relation, then derive from sign
  const mech = row.mechanism?.trim();
  const relation =
    mech && ["Activation", "Inhibition", "Phosphorylation"].includes(mech)
      ? mech
      : annotation?.relation ||
        (row.sign === "negative"
          ? "Inhibition"
          : row.sign === "positive"
            ? "Activation"
            : null);
  const entAObj = annotation?.entities?.find((e) => e.role === "A");
  const entBObj = annotation?.entities?.find((e) => e.role === "B");
  const entityA = row.regulatorName || entAObj?.name;
  const entityB = row.regulatedName || entBObj?.name;
  const contexts = annotation?.contexts || [];
  const allCtx = contexts;

  // Track active citation dots — default all ON
  const [activeSet, setActiveSet] = useState(
    () => new Set(contexts.map((_, i) => i)),
  );

  // Reset activeSet when annotation changes
  useEffect(() => {
    setActiveSet(new Set(contexts.map((_, i) => i)));
  }, [contexts]);

  const toggleCite = useCallback((ci) => {
    setActiveSet((prev) => {
      const next = new Set(prev);
      if (next.has(ci)) next.delete(ci);
      else next.add(ci);
      return next;
    });
  }, []);

  const textRef = useRef(null);
  const entityARefs = useRef([]);
  const entityBRefs = useRef([]);
  const [arcPath, setArcPath] = useState(null);

  // Recompute arc — called on render + resize
  const recomputeArc = useCallback(() => {
    if (!relation) {
      setArcPath(null);
      return;
    }
    const result = computeArc(
      textRef.current,
      entityARefs.current[0],
      entityBRefs.current[0],
    );
    setArcPath(result ? { ...result, label: relation } : null);
  }, [relation]);

  // Recompute on deps change
  useEffect(() => {
    recomputeArc();
  }, [recomputeArc, text, entityA, entityB]);

  // ResizeObserver: recompute when container resizes (e.g., window resize causes line wrap change)
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => recomputeArc());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeArc]);

  // Collect active explicit highlights (multiple simultaneous)
  const activeExplicits = allCtx
    .map((ctx, ci) => ({ ...ctx, ci }))
    .filter(
      (ctx) =>
        activeSet.has(ctx.ci) &&
        ctx.type === "explicit" &&
        ctx.start !== undefined,
    );

  // Active implicit contexts
  const activeImplicits = allCtx
    .map((ctx, ci) => ({ ...ctx, ci }))
    .filter((ctx) => activeSet.has(ctx.ci) && ctx.type !== "explicit");

  // Build text with ALL active explicit highlights simultaneously
  const renderText = () => {
    entityARefs.current = [];
    entityBRefs.current = [];

    return segments.map((seg, i) => {
      if (seg.type === "entity") {
        const isA = seg.role === "A";
        const c = isA ? COLORS.regulator : COLORS.regulated;
        const tagLabel = seg.entityType || (isA ? "regulator" : "regulated");
        return (
          <span
            key={i}
            ref={(el) => {
              if (el) (isA ? entityARefs : entityBRefs).current.push(el);
            }}
            className="cel-hl"
            style={{ background: c.bg, borderBottomColor: c.border }}
            title={`${isA ? "Regulator" : "Regulated"}: ${seg.name}`}
          >
            <span
              className="cel-hl-tag"
              style={{ background: c.bg, color: c.text, borderColor: c.border }}
            >
              {tagLabel}
            </span>
            {seg.text}
          </span>
        );
      }
      if (seg.type === "plain" && activeExplicits.length > 0) {
        // Calculate this segment's absolute position in original text
        let acc = 0;
        for (let j = 0; j < i; j++) acc += segments[j].text.length;
        const segStart = acc;
        const segEnd = segStart + seg.text.length;

        // Find ALL active explicits that overlap this segment, sorted by start
        const matches = activeExplicits
          .filter((ctx) => ctx.start < segEnd && ctx.end > segStart)
          .sort((a, b) => a.start - b.start);

        if (matches.length > 0) {
          const parts = [];
          let cursor = 0; // relative to segment start
          matches.forEach((m, mi) => {
            const rs = Math.max(0, m.start - segStart);
            const re = Math.min(seg.text.length, m.end - segStart);
            if (rs > cursor)
              parts.push(
                <span key={`${i}-g${mi}`}>{seg.text.slice(cursor, rs)}</span>,
              );
            const ctxC = COLORS[m.category] || COLORS.location;
            parts.push(
              <span
                key={`${i}-h${mi}`}
                className="cel-trigger-hl"
                style={{ background: ctxC.bg, borderBottomColor: ctxC.border }}
              >
                <span
                  className="cel-trigger-tag"
                  style={{
                    background: ctxC.bg,
                    color: ctxC.text,
                    borderColor: ctxC.border,
                  }}
                >
                  {m.label}
                </span>
                {seg.text.slice(rs, re)}
              </span>,
            );
            cursor = re;
          });
          if (cursor < seg.text.length)
            parts.push(<span key={`${i}-tail`}>{seg.text.slice(cursor)}</span>);
          return <React.Fragment key={i}>{parts}</React.Fragment>;
        }
      }
      return <span key={i}>{seg.text}</span>;
    });
  };

  if (!text) {
    return (
      <div
        className={`cel-card ${isActive ? "active" : ""}`}
        onClick={onClick}
        ref={cardRef}
      >
        <div className="cel-card-index">#{index + 1}</div>
        <div className="cel-card-empty">
          Enter a statement to see annotation...
        </div>
      </div>
    );
  }

  const hasActiveImplicits = activeImplicits.length > 0;

  return (
    <div
      className={`cel-card ${isActive ? "active" : ""}`}
      onClick={onClick}
      ref={cardRef}
    >
      <div className="cel-card-index">
        <span>#{index + 1}</span>
        {pending && <span className="cel-card-pending">Annotating...</span>}
      </div>

      <div className="cel-text-block" ref={textRef}>
        {/* SVG arc for relation */}
        {arcPath && (
          <svg className="cel-arc-svg" style={{ height: 20 }}>
            <defs>
              <marker
                id={`arrow-${index}`}
                markerWidth="6"
                markerHeight="5"
                refX="6"
                refY="2.5"
                orient="auto"
              >
                <path d="M0,0 L6,2.5 L0,5" fill={COLORS.relation.text} />
              </marker>
            </defs>
            <path
              d={`M${arcPath.x1},${arcPath.y1} Q${arcPath.midX},${arcPath.midY} ${arcPath.x2},${arcPath.y2}`}
              fill="none"
              stroke={COLORS.relation.text}
              strokeWidth="1.5"
              markerEnd={`url(#arrow-${index})`}
            />
            <text
              x={arcPath.midX}
              y={arcPath.midY - 2}
              textAnchor="middle"
              fill={COLORS.relation.text}
              fontSize="9"
              fontWeight="600"
            >
              {arcPath.label}
            </text>
          </svg>
        )}

        {/* Annotated text with implicit parentheses */}
        <div className="cel-text-content">
          {hasActiveImplicits && (
            <span
              className="cel-implicit-paren"
              style={{
                color: (COLORS[activeImplicits[0].category] || COLORS.location)
                  .text,
              }}
            >
              (
            </span>
          )}
          {renderText()}
          {hasActiveImplicits && (
            <span
              className="cel-implicit-paren"
              style={{
                color: (COLORS[activeImplicits[0].category] || COLORS.location)
                  .text,
              }}
            >
              )
            </span>
          )}

          {/* Citation dots — filled = on, dimmed = off */}
          {allCtx.length > 0 && (
            <span className="cel-citations">
              {allCtx.map((ctx, ci) => {
                const colors = COLORS[ctx.category] || COLORS.location;
                const isOn = activeSet.has(ci);
                return (
                  <span
                    key={ci}
                    className={`cel-cite ${isOn ? "" : "off"}`}
                    style={{ background: colors.text }}
                    title={`${ctx.category.replace(/_/g, " ")}: ${ctx.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCite(ci);
                    }}
                  >
                    {ci + 1}
                  </span>
                );
              })}
            </span>
          )}
        </div>

        {/* Implicit context labels below */}
        {hasActiveImplicits && (
          <div className="cel-implicit-label-row">
            {activeImplicits.map((ctx) => {
              const c = COLORS[ctx.category] || COLORS.location;
              return (
                <span
                  key={ctx.ci}
                  className="cel-implicit-label"
                  style={{
                    background: c.bg,
                    color: c.text,
                    borderColor: c.border,
                  }}
                >
                  {ctx.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function UnsavedModal({ onExport, onDiscard, onCancel }) {
  return (
    <div className="cel-modal-overlay" onClick={onCancel}>
      <div className="cel-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Unsaved Workspace Data</h3>
        <p>
          Switching to Quick mode will clear your current interaction table.
          Would you like to export your data first?
        </p>
        <div className="cel-modal-actions">
          <button className="cel-modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="cel-modal-discard" onClick={onDiscard}>
            Discard
          </button>
          <button className="cel-modal-export" onClick={onExport}>
            Export &amp; Switch
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

const CelestaApp = () => {
  const [mode, setMode] = useState("quick"); // "quick" | "workspace"
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Quick mode
  const [inputText, setInputText] = useState("");
  const [quickCards, setQuickCards] = useState([]); // [{row, annotation}]
  const quickCardIdRef = useRef(0);

  // Workspace
  const [rows, setRows] = useState([]);
  const [annotations, setAnnotations] = useState({});
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [selectedSet, setSelectedSet] = useState(new Set()); // multi-select for batch annotate
  const [hiddenCols, setHiddenCols] = useState(() => new Set(DEFAULT_HIDDEN));
  const [showColMenu, setShowColMenu] = useState(false);
  // Track cells modified by annotation (not yet saved) — Set of "rowIdx:fieldKey"
  const [dirtyCells, setDirtyCells] = useState(new Set());

  const visibleFields = INTERACTION_FIELDS.filter(
    (f) => !hiddenCols.has(f.key),
  );
  const hasDirty = dirtyCells.size > 0;

  const toggleCol = useCallback((key) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cardRefs = useRef({});
  const cardsEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    }
  }, []);

  // ---- Quick mode: submit text ----
  const handleQuickAnnotate = useCallback(async () => {
    const sentences = splitParagraphIntoSentences(inputText);
    if (!sentences.length) return;

    const cards = sentences.map((sentence) => ({
      id: quickCardIdRef.current++,
      row: { statement: sentence },
      annotation: null,
      pending: true,
    }));

    setLoading(true);
    setInputText("");
    setQuickCards((prev) => [...prev, ...cards]);
    setTimeout(autoResize, 0);
    setTimeout(
      () => cardsEndRef.current?.scrollIntoView({ behavior: "smooth" }),
      100,
    );

    try {
      for (const card of cards) {
        const result = await annotateText(card.row.statement);
        setQuickCards((prev) =>
          prev.map((item) =>
            item.id === card.id
              ? { ...item, annotation: result, pending: false }
              : item,
          ),
        );
      }
    } catch (err) {
      console.error("Annotation failed:", err);
      const failedIds = new Set(cards.map((card) => card.id));
      setQuickCards((prev) =>
        prev.map((item) =>
          failedIds.has(item.id) ? { ...item, pending: false } : item,
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [inputText, autoResize]);

  // ---- Upload xlsx → workspace ----
  const handleUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (data.length > 0) {
        setRows(data.map(mapUploadedRow));
        setAnnotations({});
        setDirtyCells(new Set());
        setSelectedIdx(0);
        setSelectedSet(new Set());
        setMode("workspace");
      }
    };
    reader.readAsArrayBuffer(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // ---- Mode switching ----
  const handleRequestQuickMode = useCallback(() => {
    if (rows.length > 0) {
      setShowUnsavedModal(true);
    } else {
      setMode("quick");
    }
  }, [rows.length]);

  const handleExportAndSwitch = useCallback(() => {
    // Export current workspace
    if (rows.length > 0) {
      const exportRows = rows.map((row, i) => {
        const ann = annotations[i];
        const out = {};
        INTERACTION_FIELDS.forEach((f) => {
          out[f.label] = row[f.key] || "";
        });
        if (ann?.relation) out["Relation (predicted)"] = ann.relation;
        if (ann?.contexts)
          ann.contexts.forEach((ctx) => {
            out[`${ctx.category} (predicted)`] = ctx.label;
          });
        return out;
      });
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CELESTA Results");
      XLSX.writeFile(wb, "celesta_annotation_results.xlsx");
    }
    setRows([]);
    setAnnotations({});
    setSelectedIdx(null);
    setShowUnsavedModal(false);
    setMode("quick");
  }, [rows, annotations]);

  const handleDiscardAndSwitch = useCallback(() => {
    setRows([]);
    setAnnotations({});
    setSelectedIdx(null);
    setShowUnsavedModal(false);
    setMode("quick");
  }, []);

  // ---- Workspace: cell edit ----
  const handleCellChange = useCallback((rowIdx, field, value) => {
    setRows((prev) => {
      const u = [...prev];
      u[rowIdx] = { ...u[rowIdx], [field]: value };
      return u;
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setSelectedIdx(rows.length);
  }, [rows.length]);

  // Annotate multiple selected rows
  const handleAnnotateSelected = useCallback(async () => {
    const indices = [...selectedSet].filter((i) => rows[i]?.statement?.trim());
    if (!indices.length) return;
    setLoading(true);
    try {
      const items = indices.map((i) => ({ text: rows[i].statement }));
      const results = await annotateTexts(items);
      const newAnn = {};
      const allChanged = new Set();
      const rowUpdates = {};
      results.forEach((result, idx) => {
        const rowIdx = indices[idx];
        newAnn[rowIdx] = result;
        const { row: filled, changed } = applyAnnotationToRow(
          rows[rowIdx],
          result,
        );
        rowUpdates[rowIdx] = filled;
        changed.forEach((k) => allChanged.add(`${rowIdx}:${k}`));
      });
      setAnnotations((prev) => ({ ...prev, ...newAnn }));
      setRows((prev) => {
        const u = [...prev];
        Object.entries(rowUpdates).forEach(([i, filled]) => {
          u[Number(i)] = filled;
        });
        return u;
      });
      setDirtyCells((prev) => new Set([...prev, ...allChanged]));
      // Scroll to first annotated card
      setTimeout(() => {
        const first = indices[0];
        if (cardRefs.current[first])
          cardRefs.current[first].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
      }, 100);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [rows, selectedSet]);

  const handleAnnotateAll = useCallback(async () => {
    const valid = rows
      .map((r, i) => (r.statement?.trim() ? i : null))
      .filter((i) => i !== null);
    if (!valid.length) return;
    setLoading(true);
    try {
      const items = valid.map((i) => ({ text: rows[i].statement }));
      const results = await annotateTexts(items);
      const newAnn = {};
      const rowUpdates = {};
      const allChanged = new Set();
      results.forEach((result, idx) => {
        const rowIdx = valid[idx];
        newAnn[rowIdx] = result;
        const { row: filled, changed } = applyAnnotationToRow(
          rows[rowIdx],
          result,
        );
        rowUpdates[rowIdx] = filled;
        changed.forEach((k) => allChanged.add(`${rowIdx}:${k}`));
      });
      setAnnotations((prev) => ({ ...prev, ...newAnn }));
      setRows((prev) => {
        const u = [...prev];
        Object.entries(rowUpdates).forEach(([i, filled]) => {
          u[Number(i)] = filled;
        });
        return u;
      });
      setDirtyCells((prev) => new Set([...prev, ...allChanged]));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [rows]);

  // Save = accept annotation changes (clear dirty highlights)
  const handleSave = useCallback(() => {
    setDirtyCells(new Set());
  }, []);

  // Export
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  const doExport = useCallback(() => {
    if (!rows.length) return;
    const exp = rows.map((row) => {
      const out = {};
      INTERACTION_FIELDS.forEach((f) => {
        out[f.label] = row[f.key] || "";
      });
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(exp);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CELESTA Results");
    XLSX.writeFile(wb, "celesta_annotation_results.xlsx");
  }, [rows]);

  const handleDownload = useCallback(() => {
    if (hasDirty) {
      setShowSavePrompt(true);
    } else {
      doExport();
    }
  }, [hasDirty, doExport]);

  // Scroll sync for workspace
  useEffect(() => {
    if (selectedIdx != null && cardRefs.current[selectedIdx])
      cardRefs.current[selectedIdx].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
  }, [selectedIdx]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleQuickAnnotate();
      }
    },
    [handleQuickAnnotate],
  );

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      accept=".xlsx,.xls"
      onChange={handleUpload}
      style={{ display: "none" }}
    />
  );

  // ====== MODE TOGGLE BAR (shared) ======
  const modeBar = (
    <div className="cel-mode-bar">
      <div className="cel-mode-toggle">
        <button
          className={mode === "quick" ? "active" : ""}
          onClick={mode === "quick" ? undefined : handleRequestQuickMode}
        >
          Quick
        </button>
        <button
          className={mode === "workspace" ? "active" : ""}
          onClick={() => {
            if (mode !== "workspace") {
              setMode("workspace");
              if (!rows.length) handleAddRow();
            }
          }}
        >
          Workspace
        </button>
      </div>
    </div>
  );

  // ====== QUICK MODE ======
  if (mode === "quick") {
    return (
      <>
        <style>{styles}</style>
        <div className="cel">
          {/* <Header title="CELESTA" gLink="https://github.com/pitt-miskov-zivanov-lab/CELESTA" /> */}
          <div className="cel-quick">
            {/* Input bar at top-center */}
            <div className="cel-input-bar">
              <p className="cel-input-description">
                Enter one sentence or paste a paragraph. Paragraphs are split
                into sentences before annotation.
              </p>
              <div className="cel-input-row">
                {fileInput}
                <button
                  className="cel-input-upload"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload interaction list (.xlsx)"
                >
                  <img src={upload_icon} alt="upload" />
                </button>
                <textarea
                  ref={textareaRef}
                  className="cel-input-text"
                  rows={1}
                  placeholder="Paste a biomedical paragraph or enter a sentence..."
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value);
                    autoResize();
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
                <button
                  className="cel-input-send"
                  onClick={handleQuickAnnotate}
                  disabled={loading || !inputText.trim()}
                  title="Annotate"
                >
                  &#10148;
                </button>
              </div>
            </div>

            {/* Annotation cards below */}
            <div className="cel-cards-scroll">
              <div className="cel-cards-inner">
                {quickCards.length > 0 && <Legend />}
                {quickCards.map((item, i) => (
                  <AnnotationCard
                    key={item.id ?? i}
                    row={item.row}
                    annotation={item.annotation}
                    index={i}
                    isActive={false}
                    pending={item.pending}
                  />
                ))}
                {loading && (
                  <div className="cel-loading">
                    <ReactLoading
                      type="balls"
                      color="#003594"
                      width={40}
                      height={40}
                    />
                  </div>
                )}
                {quickCards.length === 0 && !loading && (
                  <div className="cel-empty">
                    <div className="cel-empty-icon">📝</div>
                    <p>Annotation results will appear here.</p>
                  </div>
                )}
                <div ref={cardsEndRef} />
              </div>
            </div>
          </div>
          {modeBar}
        </div>
      </>
    );
  }

  // ====== WORKSPACE MODE ======
  return (
    <>
      <style>{styles}</style>
      <div className="cel">
        {/* <Header
          title="CELESTA"
          gLink="https://github.com/pitt-miskov-zivanov-lab/CELESTA"
        /> */}
        <div className="cel-workspace">
          <div className="cel-ws-toolbar">
            <button
              className="cel-btn"
              onClick={handleAnnotateAll}
              disabled={loading || rows.every((r) => !r.statement?.trim())}
            >
              Annotate All
            </button>
            {selectedSet.size > 0 && (
              <button
                className="cel-btn"
                onClick={handleAnnotateSelected}
                disabled={loading}
              >
                Annotate Selected ({selectedSet.size})
              </button>
            )}
            {fileInput}
            <button
              className="cel-icon-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              <img src={upload_icon} alt="upload" /> Upload .xlsx
            </button>
            <div className="cel-spacer" />
            {hasDirty && (
              <button
                className="cel-btn"
                onClick={handleSave}
                style={{
                  borderColor: "#FFB81C",
                  color: "#00205B",
                  background: "#FFB81C",
                }}
              >
                Save
              </button>
            )}
            <button
              className="cel-btn"
              onClick={handleDownload}
              disabled={!rows.length}
              style={
                rows.length ? { borderColor: "#036b45", color: "#036b45" } : {}
              }
            >
              Export
            </button>
          </div>
          <div className="cel-ws-body">
            {/* LEFT */}
            <div className="cel-left">
              <div className="cel-left-header">
                <span>Interaction List ({rows.length})</span>
                <button
                  className="cel-col-toggle-btn"
                  onClick={() => setShowColMenu((v) => !v)}
                >
                  Columns
                </button>
                {showColMenu && (
                  <div className="cel-col-dropdown">
                    {INTERACTION_FIELDS.map((f) => (
                      <label key={f.key} className="cel-col-item">
                        <input
                          type="checkbox"
                          checked={!hiddenCols.has(f.key)}
                          onChange={() => toggleCol(f.key)}
                          disabled={f.required}
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="cel-left-scroll">
                <table className="cel-edit-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>#</th>
                      {visibleFields.map((f) => (
                        <th
                          key={f.key}
                          className={f.required ? "required" : ""}
                        >
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr
                        key={i}
                        className={`${selectedSet.has(i) ? "cel-row-selected" : ""} ${!row.statement?.trim() ? "cel-row-empty" : ""}`}
                        onClick={(e) => {
                          setSelectedIdx(i);
                          if (e.ctrlKey || e.metaKey) {
                            setSelectedSet((prev) => {
                              const n = new Set(prev);
                              if (n.has(i)) n.delete(i);
                              else n.add(i);
                              return n;
                            });
                          } else {
                            setSelectedSet(new Set([i]));
                          }
                        }}
                      >
                        <td className="cel-row-num">{i + 1}</td>
                        {visibleFields.map((f) => (
                          <td key={f.key}>
                            <input
                              className={`cel-cell-input ${f.key === "statement" ? "statement-input" : ""} ${dirtyCells.has(`${i}:${f.key}`) ? "dirty" : ""}`}
                              value={row[f.key] || ""}
                              onChange={(e) =>
                                handleCellChange(i, f.key, e.target.value)
                              }
                              placeholder={f.required ? "required" : ""}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="cel-add-row-btn" onClick={handleAddRow}>
                  + Add Row
                </button>
              </div>
            </div>
            {/* RIGHT */}
            <div className="cel-right">
              <div
                className="cel-right-header"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Text Annotation</span>
                <Legend />
              </div>
              <div className="cel-right-scroll">
                {loading && (
                  <div className="cel-loading">
                    <ReactLoading
                      type="balls"
                      color="#003594"
                      width={40}
                      height={40}
                    />
                  </div>
                )}
                {!loading &&
                  rows.map((row, i) => (
                    <AnnotationCard
                      key={i}
                      row={row}
                      annotation={annotations[i] || null}
                      index={i}
                      isActive={selectedIdx === i}
                      onClick={() => setSelectedIdx(i)}
                      cardRef={(el) => {
                        cardRefs.current[i] = el;
                      }}
                    />
                  ))}
                {!loading && !rows.length && (
                  <div className="cel-empty">
                    <div className="cel-empty-icon">📝</div>
                    <p>Add a row or upload an interaction list.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {modeBar}
        {showUnsavedModal && (
          <UnsavedModal
            onExport={handleExportAndSwitch}
            onDiscard={handleDiscardAndSwitch}
            onCancel={() => setShowUnsavedModal(false)}
          />
        )}
        {showSavePrompt && (
          <div
            className="cel-modal-overlay"
            onClick={() => setShowSavePrompt(false)}
          >
            <div className="cel-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Unsaved Changes</h3>
              <p>
                Some cells have been modified by annotation (highlighted in
                yellow). Save changes before exporting?
              </p>
              <div className="cel-modal-actions">
                <button
                  className="cel-modal-cancel"
                  onClick={() => setShowSavePrompt(false)}
                >
                  Cancel
                </button>
                <button
                  className="cel-modal-discard"
                  onClick={() => {
                    setShowSavePrompt(false);
                    doExport();
                  }}
                >
                  Export Without Saving
                </button>
                <button
                  className="cel-modal-export"
                  onClick={() => {
                    handleSave();
                    setShowSavePrompt(false);
                    doExport();
                  }}
                >
                  Save &amp; Export
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CelestaApp;
